// **The rounds-owned sub-router** (`client-app` "Five-route shell with future screens stubbed",
// MODIFIED by `rounds-refresh-restore`; architecture review **F4**, artifacts review **A5**).
//
// Every path that exists only in rounds mode lives here, and the shared shell (`AppRoutes`) is exactly
// the four UI.md §3 routes again.
//
// ## Why the rounds paths left the shared table
//
// The shared shell forks modes by **substituting an opaque element at a shared path** — the discipline
// `App.tsx` states three times, "its presence IS the mode fork". Standings was the one place a
// rounds-only *path* was added to the shared table instead, and it cost a provenance sniff in the
// shell to keep it from mounting in live mode:
//
//     standingsScreen === null || (standingsScreen === undefined && lobbyController !== undefined)
//
// That is the shell deciding whether a route mounts by inspecting which *other* mode's seam was
// supplied — precisely the provenance-dependent branch UI.md §3 argues against. This change needed a
// second rounds-only path (`/practice`) and a parameterised one (`/review/:round`), and adding them
// the same way would have meant answering "what does `/practice` mount under `GameMode::Live`?", where
// every available answer is bad: the live Colony screen (nonsense), nothing (a blank route in the
// shipped live app), or a third mode sniff.
//
// So the rule is drawn at the widest seam available: **the shared shell owns the four screens both
// modes have, and this router owns the paths only rounds mode has.** Under `GameMode::Live` this
// component is never constructed, so there is no rounds path to suppress and no conditional to write.
// The route count goes six → four and the sniff is deleted rather than duplicated.
//
// This is **not** a screen-inventory change. Rounds mode still ships five screens (Home, Sandbox,
// Graded review, Standings, Summary) and live mode still has four; only which router declares a path
// moved.
//
// ## Why the review route had to move too, and not merely its parameter
//
// A first draft kept `/review` in the shared table and put only the parameter here. That does not
// work, and the reason is structural rather than stylistic: **`RoundsController` renders `AppRoutes`
// from above**, so a parameter declared in this sub-router is invisible to `useParams` inside any
// element `AppRoutes` mounts — the two routers match the same location independently and neither
// nests inside the other. Splitting the route from its parameter would have left the spec asserting
// both that the shell owns the review route and that it learns nothing about the round, which is a
// contradiction an implementer would have had to arbitrate. Moving the whole route removes the
// arbitration instead of settling it.
//
// ## Two sibling routers over one location
//
// `RoundsController` renders `<AppRoutes/>` and `<RoundsRoutes/>` as siblings. Each `Routes` matches
// the current location independently and renders `null` when nothing in it matches, so exactly one of
// the two ever produces a screen: on `/colony` the shell renders the sandbox and this renders nothing;
// on `/standings` the shell renders nothing and this renders standings. No path is declared in both.

import type { CSSProperties, ReactElement } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { ENTRY_PAGE_FRAME } from '../../ui/page-frame';
import { ROUNDS_REVIEW_ROUTE_PATTERN, resolveReviewRound } from './review-round-route';

/** The rounds-only paths, named once so the router, the navigators and the tests agree. */
export const ROUNDS_STANDINGS_ROUTE = '/standings';
/**
 * The practice sandbox's own path.
 *
 * **Distinct from the tournament sandbox's `/colony` deliberately** (`client-app` "A reload returns
 * the player to the surface they were on"): the two are independent sessions differing in credential,
 * scope, token and client, and one path naming both is exactly what forces a reload to guess — a guess
 * that is unrecoverable for whichever session it gets wrong.
 */
export const ROUNDS_PRACTICE_ROUTE = '/practice';
/**
 * The leaderboard's own path (`home-and-single-player`) — Home's third CTA.
 *
 * It belongs here rather than in the shared table for the reason the header gives: it is a rounds-only
 * path, and asking "what does `/leaderboard` mount under `GameMode::Live`?" has no good answer.
 */
export const ROUNDS_LEADERBOARD_ROUTE = '/leaderboard';
/**
 * The computer match's path — Home's second CTA until WAG-108 removed it.
 *
 * The path is **still declared and still served**, and is now reached by typing it, on the same
 * footing as `/demo`: the live pvp stack behind it is untouched and a presenter can still land here.
 * What it no longer has is an entry point on the first screen a player opens — see [`VersusLiveEntry`]
 * for the deployment fact that made offering it there a promise the server could not keep.
 *
 * The **element is a prop**, in exactly the pattern `sandboxScreen` follows: the route exists and the
 * root may substitute the screen, so the path is declared once rather than appearing when its screen
 * does.
 */
export const ROUNDS_VERSUS_ROUTE = '/versus';

export interface RoundsRoutesProps {
  /** The standings screen, already constructed by the rounds root. `null` renders the route inertly. */
  standingsScreen?: ReactElement | null;
  /** The practice sandbox screen, or `null` when no practice session is open. */
  practiceScreen?: ReactElement | null;
  /** The leaderboard screen. `null` renders the route inertly. */
  leaderboardScreen?: ReactElement | null;
  /** The computer match's screen.
   *
   * `undefined` — the default — renders [`VersusLiveEntry`], **not nothing**. An inert route was the
   * original design and it was wrong in a way only a player would notice: an inert route blanked the
   * screen with no chrome, no message and no way back but the browser button. That still holds now
   * that the path is typed rather than pressed — arriving anywhere has to render something, and a
   * blank page is the one answer that tells a person nothing.
   *
   * Pass `null` explicitly to get a genuinely inert route. */
  versusScreen?: ReactElement | null;
  /**
   * Builds the graded-review screen for the round segment the address bar carries.
   *
   * A render callback rather than an element, because the segment is only readable *inside* this
   * router — see the header. It receives the **raw** segment and resolves it itself, because
   * resolution needs the tournament's length and the root is what holds it; splitting the parse from
   * the range check across this boundary would put "fail closed while the total is unknown" in two
   * places at once.
   */
  renderReview?: (rawRound: string | undefined) => ReactElement | null;
}

/** Reads the round segment off the address bar and hands it, unexamined, to the root. */
function GradedReviewRoute({
  render,
}: {
  render?: (rawRound: string | undefined) => ReactElement | null;
}): ReactElement | null {
  const { round } = useParams();
  return render?.(round) ?? null;
}

export function RoundsRoutes({
  standingsScreen,
  practiceScreen,
  leaderboardScreen,
  versusScreen = <VersusLiveEntry />,
  renderReview,
}: RoundsRoutesProps = {}): ReactElement {
  return (
    <Routes>
      <Route path={ROUNDS_STANDINGS_ROUTE} element={standingsScreen ?? null} />
      <Route path={ROUNDS_PRACTICE_ROUTE} element={practiceScreen ?? null} />
      <Route path={ROUNDS_LEADERBOARD_ROUTE} element={leaderboardScreen ?? null} />
      <Route path={ROUNDS_VERSUS_ROUTE} element={versusScreen} />
      <Route
        path={ROUNDS_REVIEW_ROUTE_PATTERN}
        element={<GradedReviewRoute render={renderReview} />}
      />
      {/* **Rendering nothing is the expected outcome here, not a miss.** Two sibling routers over one
          location means exactly one of them matches at a time, so without this the other logs
          "No routes matched location …" on every navigation — a warning about the very arrangement
          this is built on. An explicit catch-all says the silence is intended. */}
      <Route path="*" element={null} />
    </Routes>
  );
}

/**
 * What `/versus` renders until a computer match is reachable on this deployment.
 *
 * **Not a "coming soon".** It states a deployment fact: the computer match is a live-mode surface,
 * and the pvp geometry is currently a server-wide balance replacement, so a rounds-mode server
 * cannot host one without handing the practice sandbox the pvp world and corrupting the ranked
 * ladder (`docs/dev/architecture/home-and-single-player.md`, `home-single-player-5`). This is the
 * honest answer for whoever arrives here on a server that cannot serve it.
 *
 * **WAG-108 removed the CTA that led here**, and this paragraph is why: the CTA was kept on the
 * grounds that the capability is built and the route is where it lands, but on the rounds deployment
 * a player actually opens, "the capability is built" and "this server can host it" are different
 * facts, and only the second one is what a button on the entry screen promises. The route survives
 * the CTA rather than the other way round.
 */
function VersusLiveEntry(): ReactElement {
  return (
    <section
      data-testid="versus-live-launch"
      // The same entry-surface frame the Lobby this page hands off to uses (`ui/page-frame.ts`),
      // so crossing from here into live mode is not also a change of layout.
      style={{ ...ENTRY_PAGE_FRAME, display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
    >
      <h1 style={{ fontFamily: 'var(--font-display)', margin: 0 }}>Play against the computer</h1>
      <p style={{ color: 'var(--text-muted)', margin: 0 }}>
        The computer match runs live. Enter its lobby, then create a match; AutoBee takes the second
        seat and prompts its colony while you prompt yours.
      </p>
      {/* Anchors, not buttons, deliberately: both do a **full-document** navigation, because
          crossing between the rounds root and the live root is a change of composition root and
          not a client-side route (see this file's header). They take the kit's button *look* so
          the page reads like the rest of the app; the row is what stops them running together
          into one sentence, which two bare inline anchors did. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
        <a
          data-testid="versus-live-entry"
          data-ui="button"
          href="/?mode=live&pvp=true"
          style={PRIMARY_LINK_STYLE}
        >
          Enter computer match
        </a>
        <a data-testid="versus-live-back" data-ui="button" href="/" style={SECONDARY_LINK_STYLE}>
          Back to Home
        </a>
      </div>
    </section>
  );
}

/** The kit's button geometry, shared by the two CTAs above. Mirrors `ui/components/core/Button`'s
 * primary/secondary treatment rather than importing it: these are anchors doing a full-document
 * navigation, and `Button` renders a `<button>`. */
const LINK_BUTTON_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  minHeight: 'var(--hit-target)',
  padding: '0 20px',
  borderRadius: 'var(--r-pill)',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 15,
  textDecoration: 'none',
};

const PRIMARY_LINK_STYLE: CSSProperties = {
  ...LINK_BUTTON_BASE,
  background: 'var(--accent-strong)',
  // `#FFFDF6` verbatim from `Button`'s primary variant — the kit carries no on-accent token, and
  // inventing one here would put a second definition of the same colour in the codebase.
  color: '#FFFDF6',
  border: '1px solid var(--accent-strong)',
};

const SECONDARY_LINK_STYLE: CSSProperties = {
  ...LINK_BUTTON_BASE,
  background: 'var(--surface-card)',
  color: 'var(--ink-1)',
  border: '1px solid var(--line-strong)',
};

// Re-exported so a consumer of the sub-router needs one import for the route vocabulary.
export { ROUNDS_REVIEW_ROUTE_PATTERN, resolveReviewRound };
